import React, { useEffect } from "react";

type Modifiers = {
	mono: boolean;
};

const modifierOrder: (keyof Modifiers)[] = ["mono"];
const SPORT_FALLBACK = "/assets/images/sportFallback.svg";
const INTERNATIONAL = "/assets/images/international.svg";

export type OfferIconContentProps = (
	| {
			type: "sport";
			sport: string;
	  }
	| {
			type: "category";
			sport: string;
			category: string;
	  }
	| {
			type: "tournament";
			catOverSportClass?: string;
			sport: string;
			category: string;
			tournament: string;
			mobile?: boolean;
	  }
	| {
			type: "topTournament";
			topTournament: string;
	  }
) & {
	iconUrl?: string;
	modifiers?: Partial<Modifiers>;
};

export type OfferIconStylingProps = {
	className?: string;
	wrapperClassName?: string;
	treeIcon?: boolean; // NO LONGER NEEDED
};

export type OfferIconProps = OfferIconContentProps & OfferIconStylingProps;

const OfferIcons = React.memo(function OfferIcons(props: OfferIconProps) {
	const sources = useGetSources(props);

	if (props.type === "tournament" && props.catOverSportClass) {
		return (
			<TournamentWithSportCatFallback
				tournamentImg={sources[0]}
				sport={props.sport}
				category={props.category}
				wrapperClassName={props.wrapperClassName}
				className={props.className}
				catOverSportClass={props.catOverSportClass}
				mobile={props.mobile}
			/>
		);
	}
	return (
		<OfferIcon
			sources={sources}
			wrapperClassName={props.wrapperClassName}
			className={props.className}
		/>
	);
});

function TournamentWithSportCatFallback(props: {
	tournamentImg: string;
	sport: string;
	category: string;
	catOverSportClass: string;
	wrapperClassName?: string;
	className?: string;
	mobile?: boolean;
}) {
	const [ownIconExists, setOwnIconExists] = React.useState(true);

	if (ownIconExists)
		return (
			<picture className={props.wrapperClassName}>
				<img
					// @ts-expect-error
					loading="lazy"
					src={props.tournamentImg}
					style={{ opacity: "0" }}
					className={props.className}
					onError={() => setOwnIconExists(false)}
					onLoad={({ currentTarget }) => {
						currentTarget.style.opacity = "";
					}}
				/>
			</picture>
		);

	return (
		<TournamentSportCatFallback
			sport={props.sport}
			category={props.category}
			mobile={props.mobile}
			wrapperClassName={props.wrapperClassName}
			catOverSportClass={props.catOverSportClass}
			className={props.className}
		/>
	);
}

function TournamentSportCatFallback({
	sport,
	category,
	mobile,
	catOverSportClass,
	wrapperClassName,
	className,
}: {
	sport: string;
	category: string;
	mobile?: boolean;
	catOverSportClass?: string;
	wrapperClassName?: string;
	className?: string;
}) {
	const [sportSources, catSources] = React.useMemo(
		() => [getSportUrls(sport), getCategoryUrls(sport, category)],
		[sport, category]
	);

	return (
		<>
			<OfferIcon
				sources={sportSources}
				wrapperClassName={wrapperClassName}
				className={className}
			/>
			{/* Mobile should display only sport OR specific icon */}
			{!mobile && (
				<OfferIcon
					sources={catSources}
					wrapperClassName={catOverSportClass}
					className={className}
				/>
			)}
		</>
	);
}

function OfferIcon(props: {
	sources: string[];
	wrapperClassName?: string;
	className?: string;
}) {
	const [srcIdx, setSrcIdx] = React.useState(0);
	const [isTransparent, setIsTransparent] = React.useState(true);

	useEffect(() => {
		return () => {
			setSrcIdx(0);
		};
	}, [props.sources]);

	return (
		<picture className={props.wrapperClassName}>
			<img
				// @ts-expect-error
				loading="lazy"
				src={props.sources[srcIdx]}
				style={isTransparent ? { opacity: "0" } : {}}
				className={props.className}
				onError={() => {
					if (srcIdx < props.sources.length - 1) {
						setSrcIdx((idx) => idx + 1);
					}
				}}
				onLoad={() => setIsTransparent(false)}
			/>
		</picture>
	);
}

function useGetSources(params: OfferIconProps): string[] {
	const modifiers = React.useMemo(
		() => getModifiers(params.modifiers),
		[params.modifiers]
	);
	const sources = React.useMemo(
		() => getBaseSources(params, modifiers),
		[params, modifiers]
	);
	return sources;
}

function trimAmateur(val: string): string {
	//remove amateur from name to avoid uploading separate icons for every "-amateur" category
	return val && val.includes("amateur") ? val.replace("-amateur", "") : val;
}

function getModifiers(modifiers?: Partial<Modifiers>): string {
	if (!modifiers) {
		return "";
	}

	return modifierOrder.reduce((acc, x) => {
		if (modifiers[x]) {
			return acc + "--" + x;
		}
		return acc;
	}, "");
}

function getBaseSources(params: OfferIconProps, modifiers: string): string[] {
	const iconUrls = [];

	if (params.iconUrl) {
		iconUrls.push(params.iconUrl);
	}

	if (params.type === "tournament") {
		iconUrls.push(
			...getTournamentUrls(
				params.sport,
				params.category,
				params.tournament,
				modifiers
			)
		);
	}
	if (params.type === "category" || params.type === "tournament") {
		iconUrls.push(
			...getCategoryUrls(params.sport, params.category, modifiers)
		);
	}
	if (
		params.type === "sport" ||
		params.type === "category" ||
		params.type === "tournament"
	) {
		iconUrls.push(...getSportUrls(params.sport, modifiers));
	}
	return iconUrls;
}

function getTournamentUrls(
	sport: string,
	category: string,
	tournament: string,
	modifiers?: string
) {
	const urls: string[] = [];
	const tournamentLC = tournament.toLowerCase();
	const categoryLC = category.toLowerCase();

	if (modifiers != "") {
		urls.push(
			`${WEBPACK_CDN_ROOT_URL}/icons/sport/${sport}/${categoryLC}/${tournamentLC}/${tournamentLC}${modifiers?.toLowerCase()}.svg`
		);
	}
	urls.push(
		`${WEBPACK_CDN_ROOT_URL}/icons/sport/${sport}/${categoryLC}/${tournamentLC}/${tournamentLC}.svg`
	);
	return urls;
}

function getCategoryUrls(sport: string, category: string, modifiers?: string) {
	const urls: string[] = [];
	const catLC = category.toLowerCase();

	if (modifiers) {
		urls.push(
			`${WEBPACK_CDN_ROOT_URL}/icons/sport/${sport}/${catLC}/${catLC}${modifiers.toLowerCase()}.svg`
		);
	}
	urls.push(
		`${WEBPACK_CDN_ROOT_URL}/icons/sport/${sport}/${trimAmateur(
			catLC
		)}/${trimAmateur(catLC)}.svg`
	);
	urls.push(INTERNATIONAL);

	return urls;
}

function getSportUrls(sport: string, modifiers?: string) {
	const urls: string[] = [];
	if (modifiers) {
		urls.push(
			`${WEBPACK_CDN_ROOT_URL}/icons/sport/${sport}/${sport}${modifiers}.svg`
		);
	}
	urls.push(`${WEBPACK_CDN_ROOT_URL}/icons/sport/${sport}/${sport}.svg`);
	urls.push(SPORT_FALLBACK);

	return urls;
}

export default OfferIcons;
